import { useState, useEffect, useCallback, useRef } from 'react';
import { mockStatus, mockStats, mockLaunches, mockLastBuyback, STEPS } from '../data/mock';
import { fetchPublicStatus, fetchPublicFeed, openPublicStream } from '../lib/backend';

const USE_MOCK = (process.env.REACT_APP_USE_MOCK || 'false').toLowerCase() === 'true';

// Simulate live progress updates in mock mode
const useMockSimulation = (initialStatus, setStatus) => {
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!USE_MOCK) return;

    let currentStepIndex = STEPS.findIndex(s => s.key === initialStatus.current_step);
    let stepProgress = initialStatus.steps[currentStepIndex]?.progress || 0;

    intervalRef.current = setInterval(() => {
      setStatus(prev => {
        const newSteps = [...prev.steps];
        const currentStep = newSteps[currentStepIndex];
        
        if (!currentStep) return prev;

        if (currentStep.progress < 100) {
          // Increment current step progress
          const increment = Math.random() * 8 + 2;
          currentStep.progress = Math.min(100, currentStep.progress + increment);
          currentStep.status = 'running';
        } else {
          // Move to next step
          currentStep.status = 'done';
          currentStepIndex++;
          
          if (currentStepIndex < newSteps.length) {
            newSteps[currentStepIndex].status = 'running';
            newSteps[currentStepIndex].progress = 0;
          } else {
            // Cycle complete, restart
            currentStepIndex = 0;
            newSteps.forEach((step, i) => {
              step.status = i === 0 ? 'running' : 'pending';
              step.progress = 0;
            });
          }
        }

        // Calculate global progress
        const completedSteps = newSteps.filter(s => s.status === 'done').length;
        const currentProgress = newSteps[currentStepIndex]?.progress || 0;
        const globalProgress = Math.round(
          ((completedSteps + currentProgress / 100) / newSteps.length) * 100
        );

        const currentStepData = STEPS[currentStepIndex];

        return {
          ...prev,
          steps: newSteps,
          global_progress: globalProgress,
          current_step: currentStepData?.key || prev.current_step,
          current_action: currentStepData?.label || prev.current_action,
        };
      });
    }, 1500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [initialStatus, setStatus]);
};

function normalizeStatus(s) {
  if (!s || typeof s !== 'object') return s;
  return {
    ...s,
    next_cycle_eta_seconds: typeof s.next_cycle_eta_seconds === 'number' ? s.next_cycle_eta_seconds : 0,
    next_cycle_at_ms: typeof s.next_cycle_at_ms === 'number' ? s.next_cycle_at_ms : null,
    steps: Array.isArray(s.steps) ? s.steps : [],
    global_progress: typeof s.global_progress === 'number' ? s.global_progress : 0,
  };
}

export const usePublicStream = () => {
  const [status, setStatus] = useState(USE_MOCK ? mockStatus : {
    current_action: 'Idle',
    global_progress: 0,
    current_step: null,
    steps: [],
    next_cycle_eta_seconds: 0,
    next_cycle_at_ms: null,
  });
  const [stats, setStats] = useState(USE_MOCK ? mockStats : {
    tokens_launched_24h: 0,
    tokens_launched_7d: 0,
    tokens_launched_all: 0,
    buybacks_eth_24h: 0,
    buybacks_eth_7d: 0,
    buybacks_eth_all: 0,
    treasury_eth_balance: 0,
    last_cycle_duration_seconds: 0,
  });
  const [launches, setLaunches] = useState(USE_MOCK ? mockLaunches : []);
  const [lastBuyback, setLastBuyback] = useState(USE_MOCK ? mockLastBuyback : null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);

  // Use mock simulation when in mock mode
  useMockSimulation(mockStatus, setStatus);

  const connectToStream = useCallback(() => {
    if (USE_MOCK) {
      setIsConnected(true);
      return;
    }

    // Real SSE connection (backend emits: hello, status, feed, metrics)
    try {
      eventSourceRef.current = openPublicStream();

      eventSourceRef.current.onopen = () => {
        setIsConnected(true);
        setError(null);
      };

      eventSourceRef.current.addEventListener('status', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data && typeof data === 'object') setStatus(normalizeStatus(data));
        } catch (e) {
          console.error('Error parsing status:', e);
        }
      });

      // Backend only sends a ping payload for feed updates; refetch feed + revealed launches.
      eventSourceRef.current.addEventListener('feed', async () => {
        try {
          await fetchPublicFeed({ limit: 50 });
        } catch {
          // ignore
        }

        // Refresh launches without clearing existing UI state.
        try {
          const base = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.trim()) || window.location.origin;
          const lr = await fetch(`${base}/api/public/launches?limit=20`);
          if (lr.ok) {
            const j = await lr.json();
            const items = j?.items || [];
            const mapped = items
              .map((x) => ({
                name: x.name,
                ticker: x.symbol,
                contract_url: x.contract_url,
                timestamp: x.ended_at || x.started_at,
                image_url: x.image_url,
              }))
              .filter((x) => x.name && x.ticker);

            if (mapped.length > 0) setLaunches(mapped);
          }
        } catch {
          // ignore
        }
      });

      eventSourceRef.current.addEventListener('metrics', (event) => {
        try {
          const data = JSON.parse(event.data);
          // Map backend metrics -> UI stats shape (best-effort)
          const total = Number(data?.total_tokens_deployed || 0);
          setStats(prev => ({
            ...prev,
            tokens_launched_all: total,
          }));
          if (data?.last_buyback_tx) {
            setLastBuyback({
              tx_hash: data.last_buyback_tx,
              amount_eth: 0,
              timestamp: data.updated_at || new Date().toISOString(),
            });
          }
        } catch (e) {
          console.error('Error parsing metrics:', e);
        }
      });

      eventSourceRef.current.onerror = () => {
        setIsConnected(false);
        setError('Connection lost. Retrying...');
        eventSourceRef.current?.close();
        setTimeout(connectToStream, 5000);
      };
    } catch (e) {
      setError('Failed to connect to stream');
      setIsConnected(false);
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    if (USE_MOCK) return;

    try {
      const ac = new AbortController();
      const s = await fetchPublicStatus(ac.signal);
      setStatus(normalizeStatus(s));

      // Map backend metrics -> UI stats (best-effort)
      const m = s?.metrics || {};
      setStats((prev) => ({
        ...prev,
        tokens_launched_24h: Number(m.tokens_launched_24h || 0),
        tokens_launched_7d: Number(m.tokens_launched_7d || 0),
        tokens_launched_all: Number(m.total_tokens_deployed || 0),
        buybacks_eth_24h: Number(m.buybacks_eth_24h || 0),
        buybacks_eth_7d: Number(m.buybacks_eth_7d || 0),
        buybacks_eth_all: Number(m.buybacks_eth_all || 0),
        treasury_eth_balance: Number(m.treasury_eth_balance || 0),
        last_cycle_duration_seconds: Number(m.last_cycle_duration_seconds || 0),
      }));

      // Feed is public but does not include token reveal details.
      await fetchPublicFeed({ limit: 50, signal: ac.signal });

      // Launches (revealed-only)
      try {
        const base = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.trim()) || window.location.origin;
        const lr = await fetch(`${base}/api/public/launches?limit=20`, { signal: ac.signal });
        if (lr.ok) {
          const j = await lr.json();
          const items = j?.items || [];
          // Map backend shape -> UI shape
          setLaunches(items.map((x) => ({
            name: x.name,
            ticker: x.symbol,
            contract_url: x.contract_url,
            timestamp: x.ended_at || x.started_at,
            image_url: x.image_url,
          })).filter((x) => x.name && x.ticker));
        }
      } catch {}
    } catch (e) {
      console.error('Error fetching initial data:', e);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
    connectToStream();

    // Poll status periodically so metrics updated by ops cron (separate process)
    // still reflect in the UI without waiting for the next engine cycle.
    let poll = null;
    if (!USE_MOCK) {
      poll = setInterval(() => {
        fetchInitialData();
      }, 60_000);
    }

    return () => {
      eventSourceRef.current?.close();
      if (poll) clearInterval(poll);
    };
  }, [fetchInitialData, connectToStream]);

  return {
    status,
    stats,
    launches,
    lastBuyback,
    isConnected,
    error,
    isMockMode: USE_MOCK,
  };
};

export default usePublicStream;
